import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useId, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

import { getModule, liveModules } from "../app/modules";
import { useProject } from "../app/projectContext";
import { StudyProjectLine } from "../components/StudyProjectLine";
import { EMPTY_VALUE, formatUtc } from "../components/report/reportFormat";
import type { CalculationRunSummary } from "../services/calculationRun";
import {
  APPROVAL_STATUSES,
  getReport,
  listReports,
  RUN_STATUSES,
  type ApprovalStatusFilter,
  type ReportRunFilter,
  type RunStatusFilter,
} from "../services/report";
import { describeReportFailure } from "./RunReportPage";

// The reports landing page (EOS-13, design note section 5; the module's route): the stored
// runs across modules, newest first as the server lists them, with "Open report" per row. The
// scope is the project revision chosen in the shell's topbar; without one, every run the
// organization may read (the server scopes the list). The approver of an APPROVED run is read
// from that run's report document (the run summary carries the approval state only, and the
// approver's name is derived by the report service, commit 8a) - never from the browser.
//
// F2 (2026-10-15): the list is a page of the server's keyset (created_at, id, newest first),
// never the whole set. Filters - module, a key search (case-insensitive prefix), run status,
// approval status - apply when the form is submitted; a change empties the list and asks the
// server again (the query key carries the filters, so an answer to an earlier filter never
// lands in the new list). "Load more" asks for the page after the last one through its cursor;
// the count line says how many of the filtered set are shown. No filter is preselected.

const PAGE_SIZE = 100;

const DESIGN_CHECK_WORDS: Record<string, string> = {
  DESIGN_CHECK_PASSED: "Design check passed",
  DESIGN_CHECK_FAILED: "Design check failed",
  NO_STANDARD_SIZE_AVAILABLE: "No standard size available",
  REVIEW_REQUIRED: "Engineering review required",
  VALID: "Calculated",
  WARNING: "Calculated with warnings",
  NO_SOLUTION: "Not met - no solution",
  CALCULATED: "Study calculated",
  INDETERMINATE: "Result indeterminate",
};

const RUN_STATUS_WORDS: Record<RunStatusFilter, string> = {
  DRAFT: "Draft",
  COMPLETED: "Completed",
  FAILED: "Failed",
};

const APPROVAL_STATUS_WORDS: Record<ApprovalStatusFilter, string> = {
  NOT_SUBMITTED: "Not submitted (DRAFT)",
  PENDING: "Pending",
  APPROVED: "Approved",
  REJECTED: "Rejected",
};

/** The filters a reader may set; empty strings mean "any". */
export type ReportListFilters = {
  module_code: string;
  key_prefix: string;
  run_status: "" | RunStatusFilter;
  approval_status: "" | ApprovalStatusFilter;
};

export const NO_FILTERS: ReportListFilters = {
  module_code: "",
  key_prefix: "",
  run_status: "",
  approval_status: "",
};

/** The query of the first page for a scope and the filters; the cursor is added per page. */
export function listFilter(
  revisionId: string | null,
  filters: ReportListFilters,
): ReportRunFilter {
  const filter: ReportRunFilter = { limit: PAGE_SIZE };
  if (revisionId !== null) {
    filter.project_revision_id = revisionId;
  }
  if (filters.module_code !== "") {
    filter.module_code = filters.module_code;
  }
  const prefix = filters.key_prefix.trim();
  if (prefix !== "") {
    filter.calculation_key_prefix = prefix;
  }
  if (filters.run_status !== "") {
    filter.run_status = filters.run_status;
  }
  if (filters.approval_status !== "") {
    filter.approval_status = filters.approval_status;
  }
  return filter;
}

function projectText(run: CalculationRunSummary): string {
  return run.project === null
    ? "Unassigned"
    : `${run.project.project_code} — ${run.project.project_name} · ${run.project.revision_label}`;
}

function moduleText(run: CalculationRunSummary): string {
  const module = getModule(run.module_code);
  return module === undefined ? run.module_code : `${run.module_code} — ${module.name}`;
}

/** The approver and the time of an APPROVED run, from its report document. */
function ApprovalRecord({ runId }: { runId: string }) {
  const query = useQuery({
    queryKey: ["report", { kind: "run", runId }],
    queryFn: ({ signal }) => getReport(runId, signal),
  });
  if (query.isPending) {
    return (
      <>
        <td data-field="approved-by">…</td>
        <td data-field="approved-at">…</td>
      </>
    );
  }
  if (query.isError) {
    return (
      <>
        <td data-field="approved-by">{EMPTY_VALUE}</td>
        <td data-field="approved-at">{EMPTY_VALUE}</td>
      </>
    );
  }
  const { signatures } = query.data;
  return (
    <>
      <td data-field="approved-by">{signatures.approved_by_name ?? "approver not on record"}</td>
      <td data-field="approved-at">
        {signatures.approved_at === null ? EMPTY_VALUE : formatUtc(signatures.approved_at)}
      </td>
    </>
  );
}

function RunRow({ run }: { run: CalculationRunSummary }) {
  const approved = run.approval_status === "APPROVED";
  return (
    <tr data-run-id={run.id}>
      <td>{moduleText(run)}</td>
      <td>
        {run.calculation_key} · revision {run.revision_number}
      </td>
      <td>{projectText(run)}</td>
      <td>{formatUtc(run.calculated_at)}</td>
      <td data-result-status={run.design_check_status}>
        {DESIGN_CHECK_WORDS[run.design_check_status] ?? run.design_check_status}
      </td>
      <td>
        <span data-report-status={approved ? "APPROVED" : "DRAFT"}>
          {approved ? "Approved" : "DRAFT"}
        </span>
      </td>
      {approved ? (
        <ApprovalRecord runId={run.id} />
      ) : (
        <>
          <td data-field="approved-by">{EMPTY_VALUE}</td>
          <td data-field="approved-at">{EMPTY_VALUE}</td>
        </>
      )}
      <td>
        <Link to={`/reports/runs/${run.id}`}>Open report</Link>
      </td>
    </tr>
  );
}

/** The filter form: plain controls, applied on submit (Enter or the button), cleared at once. */
function ReportFilters({
  applied,
  onApply,
}: {
  applied: ReportListFilters;
  onApply: (filters: ReportListFilters) => void;
}) {
  const [draft, setDraft] = useState<ReportListFilters>(applied);
  const id = useId();

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    onApply(draft);
  }

  function clear(): void {
    setDraft(NO_FILTERS);
    onApply(NO_FILTERS);
  }

  return (
    <form aria-label="Filter the stored runs" data-reports-filters onSubmit={submit}>
      <div data-reports-filter-fields>
        {/* Each label stands beside its control (htmlFor), so its name is the label's words alone. */}
        <div data-reports-filter="module">
          <label htmlFor={`${id}-module`}>Module</label>
          <select
            id={`${id}-module`}
            name="module_code"
            value={draft.module_code}
            onChange={(event) => setDraft({ ...draft, module_code: event.target.value })}
          >
            <option value="">All modules</option>
            {liveModules().map((module) => (
              <option key={module.code} value={module.code}>
                {module.code} — {module.name}
              </option>
            ))}
          </select>
        </div>
        <div data-reports-filter="key">
          <label htmlFor={`${id}-key`}>Study key starts with</label>
          <input
            id={`${id}-key`}
            name="key_prefix"
            type="search"
            autoComplete="off"
            value={draft.key_prefix}
            onChange={(event) => setDraft({ ...draft, key_prefix: event.target.value })}
          />
        </div>
        <div data-reports-filter="run-status">
          <label htmlFor={`${id}-run-status`}>Run status</label>
          <select
            id={`${id}-run-status`}
            name="run_status"
            value={draft.run_status}
            onChange={(event) =>
              setDraft({ ...draft, run_status: event.target.value as "" | RunStatusFilter })
            }
          >
            <option value="">Any</option>
            {RUN_STATUSES.map((status) => (
              <option key={status} value={status}>
                {RUN_STATUS_WORDS[status]}
              </option>
            ))}
          </select>
        </div>
        <div data-reports-filter="approval">
          <label htmlFor={`${id}-approval`}>Approval</label>
          <select
            id={`${id}-approval`}
            name="approval_status"
            value={draft.approval_status}
            onChange={(event) =>
              setDraft({
                ...draft,
                approval_status: event.target.value as "" | ApprovalStatusFilter,
              })
            }
          >
            <option value="">Any</option>
            {APPROVAL_STATUSES.map((status) => (
              <option key={status} value={status}>
                {APPROVAL_STATUS_WORDS[status]}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div data-reports-filter-actions>
        <button type="submit">Apply filters</button>
        <button type="button" onClick={clear}>
          Clear filters
        </button>
      </div>
    </form>
  );
}

export function ReportsPage() {
  const { state } = useProject();
  const revision =
    state.status === "selected" || state.status === "read-only" ? state.revision : null;
  const [filters, setFilters] = useState<ReportListFilters>(NO_FILTERS);
  const filter = listFilter(revision === null ? null : revision.id, filters);
  const filtered = Object.keys(listFilter(null, filters)).length > 1;

  const query = useInfiniteQuery({
    queryKey: ["reports", "list", filter],
    queryFn: ({ pageParam, signal }) =>
      listReports(pageParam === undefined ? filter : { ...filter, cursor: pageParam }, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
  });

  const pages = query.data?.pages ?? [];
  const items = pages.flatMap((page) => page.items);
  const totalCount = pages.length === 0 ? 0 : pages[pages.length - 1]!.total_count;
  const moreError = query.isFetchNextPageError ? query.error : null;

  return (
    <main data-reports-page>
      <header>
        <p>EOS-13 · Deliverables</p>
        <h1>Reports</h1>
        <p>
          Printable calculation reports of stored runs, newest first. Choose a project revision in
          the top bar to see its runs alone; every report is read from the run as stored.
        </p>
      </header>
      <StudyProjectLine />
      <ReportFilters applied={filters} onApply={setFilters} />

      {query.isPending ? (
        <p role="status" data-reports-state="loading">
          Loading the stored runs…
        </p>
      ) : query.isError ? (
        <div role="alert" data-reports-state="error">
          <p>{describeReportFailure(query.error).message}</p>
        </div>
      ) : items.length === 0 ? (
        <p data-reports-state="empty">
          {filtered
            ? "No stored run matches these filters."
            : revision === null
              ? "No stored runs."
              : "No stored runs in this revision."}
        </p>
      ) : (
        <>
          <p data-reports-count={items.length} data-reports-total={totalCount}>
            Showing {items.length} of {totalCount} stored runs
          </p>
          <div data-table-scroll>
            <table aria-label="Stored runs" data-reports-state="ready">
              <thead>
                <tr>
                  <th scope="col">Module</th>
                  <th scope="col">Study</th>
                  <th scope="col">Project</th>
                  <th scope="col">Calculated (UTC)</th>
                  <th scope="col">Design check</th>
                  <th scope="col">Status</th>
                  <th scope="col">Approved by</th>
                  <th scope="col">Approved at (UTC)</th>
                  <th scope="col">Report</th>
                </tr>
              </thead>
              <tbody>
                {items.map((run) => (
                  <RunRow key={run.id} run={run} />
                ))}
              </tbody>
            </table>
          </div>
          {query.hasNextPage ? (
            <p data-reports-more>
              <button
                type="button"
                onClick={() => void query.fetchNextPage()}
                disabled={query.isFetchingNextPage}
              >
                {query.isFetchingNextPage ? "Loading more…" : "Load more"}
              </button>
            </p>
          ) : null}
          {moreError !== null ? (
            <p role="alert" data-reports-state="more-error">
              {describeReportFailure(moreError).message}
            </p>
          ) : null}
        </>
      )}
    </main>
  );
}

export default ReportsPage;
